import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import type { Application, UnitClass } from '../../data/types';
import { applicationPayment } from '../../domain/raise/raiseCalc';
import { date, dateTime, DEMO_TODAY, money } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import DataTable from '../../ui/DataTable';
import { useToast } from '../../ui/feedback';
import { KeyValueGrid, SectionCard, StatusChip } from '../../ui/primitives';

const PAYMENT_LABELS = { NOT_PAID: 'Not paid', PART_PAID: 'Part paid', PAID: 'Paid' } as const;
const PAYMENT_TONES = { NOT_PAID: 'warning', PART_PAID: 'info', PAID: 'success' } as const;

export function PaymentStatusChip({ status }: { status: keyof typeof PAYMENT_LABELS }) {
  return <StatusChip status={status} label={PAYMENT_LABELS[status]} tone={PAYMENT_TONES[status]} />;
}

export default function ApplicationPaymentPanel({
  application,
  unitClass,
}: {
  application: Application;
  unitClass?: UnitClass;
}) {
  const { dispatch } = useRegistry();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [amount, setAmount] = useState('');
  const [receivedOn, setReceivedOn] = useState(DEMO_TODAY);
  const payment = applicationPayment(application, unitClass);
  const instruction = application.paymentInstruction;
  const open = !['ISSUED', 'REJECTED', 'WITHDRAWN'].includes(application.status);

  return (
    <SectionCard
      title="Payment"
      subtitle="Payment instruction, reminders and money received"
      action={<PaymentStatusChip status={payment.status} />}
    >
      <KeyValueGrid
        columns={3}
        items={[
          { label: 'Payable now', value: money(payment.payableNow, true) },
          { label: 'Received', value: money(payment.received, true) },
          { label: 'Outstanding', value: money(payment.outstanding, true) },
          ...(payment.payableLater > 0
            ? [{ label: 'Payable later (partly paid)', value: money(payment.payableLater, true) }]
            : []),
          { label: 'BPAY / EFT CRN', value: instruction?.crn ?? 'Issued on submission' },
          { label: 'Instruction sent', value: dateTime(instruction?.sentAt) },
          {
            label: 'Reminders',
            value: instruction?.reminders.length
              ? instruction.reminders.map((stamp) => date(stamp)).join(', ')
              : 'None sent',
          },
        ]}
      />
      {instruction && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
          {instruction.fileName}
        </Typography>
      )}
      {open && application.submittedAt && (
        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              dispatch({ type: 'APPLICATION_REGENERATE_INSTRUCTION', id: application.id });
              toast('Fresh payment instruction sent');
            }}
          >
            Regenerate instruction
          </Button>
          {payment.status !== 'PAID' && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                dispatch({ type: 'APPLICATION_PAYMENT_REMINDER', id: application.id });
                toast('Payment reminder sent');
              }}
            >
              Send payment reminder
            </Button>
          )}
          {payment.status !== 'PAID' && (
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                setAmount(String(payment.outstanding));
                setAdding(true);
              }}
            >
              Record payment
            </Button>
          )}
        </Stack>
      )}
      <Box sx={{ mt: 2 }}>
        <DataTable
          dense
          rows={application.receipts ?? []}
          rowKey={(row) => row.id}
          emptyMessage="No payments received yet"
          columns={[
            { key: 'date', label: 'Received', render: (row) => date(row.date) },
            {
              key: 'amount',
              label: 'Paid',
              align: 'right',
              render: (row) => money(row.amount, true),
            },
            { key: 'reference', label: 'Reference', render: (row) => row.reference },
            {
              key: 'source',
              label: 'Recorded by',
              render: (row) => (row.source === 'BANK_FEED' ? 'Bank feed' : row.recordedBy),
            },
          ]}
        />
      </Box>
      <Dialog open={adding} onClose={() => setAdding(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Record a payment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Amount received ($)"
              value={amount}
              onChange={(event) => setAmount(event.target.value.replace(/[^\d.]/g, ''))}
              helperText={`Outstanding ${money(payment.outstanding, true)}. Part payments are fine.`}
            />
            <TextField
              label="Date received"
              type="date"
              value={receivedOn}
              onChange={(event) => setReceivedOn(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!(Number(amount) > 0)}
            onClick={() => {
              dispatch({
                type: 'APPLICATION_RECORD_RECEIPT',
                applicationId: application.id,
                amount: Number(amount),
                date: receivedOn,
                reference: instruction?.crn ?? application.reference,
              });
              toast('Payment recorded');
              setAdding(false);
            }}
          >
            Record
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
}
